import axios from 'axios'
import React, { createContext, useContext, useState, useCallback } from 'react'

import type { SavingsResponse } from '../types/Savings'
import type { PropsWithChildren } from 'react'

interface SavingsContextProps {
  savings?: Savings
  getSavings?: () => Promise<void>
}

export type Savings = QueryStatus<SavingsResponse>

interface QueryStatus<T> {
  loading: boolean
  error: boolean
  data: T | undefined
}

const SavingsContext = createContext<SavingsContextProps | undefined>(undefined)

export const SavingsContextProvider = ({
  children,
}: PropsWithChildren<SavingsContextProps>) => {
  const [savings, setSavings] = useState<Savings>({
    loading: false,
    error: false,
    data: undefined,
  })

  const getSavings = useCallback(async () => {
    setSavings({ loading: true, error: false, data: undefined })

    try {
      const { data } = await axios.get<SavingsResponse>(
        '/api/loyalty/customer/savings',
        {
          params: { random: Math.random() }, // Prevent caching
        }
      )

      setSavings({ loading: false, error: false, data })
    } catch {
      setSavings({ loading: false, error: true, data: undefined })
    }
  }, [])

  return (
    <SavingsContext.Provider value={{ savings, getSavings }}>
      {children}
    </SavingsContext.Provider>
  )
}

export const useSavingsContext = () => {
  const context = useContext(SavingsContext)

  if (!context) {
    throw new Error(
      'useSavingsContext must be used within a SavingsContextProvider'
    )
  }

  return context
}
